import { generateClient } from "aws-amplify/data";
import type { Schema } from "amplify/data/resource";

const client = generateClient<Schema>();

export type FeeMode = "freepay" | "customFee" | "none";

export interface FeeModeState {
  mode: FeeMode;
  /** True when both fee flags are literally set, a legacy state deriveFeeMode
   * alone can't reveal since it resolves the pair to "freepay". */
  bothOn: boolean;
  /** The Freepay debit toggle. Only meaningful in freepay mode; the other two
   * mode batches force it off. */
  freepayDebit: boolean;
}

export interface MethodRate {
  percent: number;
  flatCents: number;
}

export interface CustomFeeSettings {
  name: string;
  creditEnabled: boolean;
  credit: MethodRate;
  debitEnabled: boolean;
  debit: MethodRate;
}

const KEYS = {
  freepayCredit: "freepay_credit_enabled",
  freepayDebit: "freepay_debit_enabled",
  enabled: "custom_fee_enabled",
  name: "custom_fee_name",
  // Credit's rate. Every terminal build reads and writes these, so they must
  // never be renamed: a rate saved under a new name would be invisible to
  // every terminal still on an older build.
  percent: "custom_fee_percent",
  flatCents: "custom_fee_flat_cents",
  creditEnabled: "custom_fee_credit_enabled",
  debitEnabled: "custom_fee_debit_enabled",
  debitPercent: "custom_fee_debit_percent",
  debitFlatCents: "custom_fee_debit_flat_cents",
} as const;

/** Terminal default per-method rates for a business with no stored fee. */
const DEFAULTS: CustomFeeSettings = {
  name: "Service fee",
  creditEnabled: true,
  credit: { percent: 2.49, flatCents: 10 },
  debitEnabled: true,
  debit: { percent: 0, flatCents: 10 },
};

/** The exclusive boolean batch for each mode — mirrors the terminal exactly.
 *
 * Each key is its own row, so the batch is written one round trip at a time and
 * can stop halfway. The order is the safety mechanism, not presentation: every
 * key that turns a fee OFF comes before the one that turns the target fee ON,
 * so an interrupted switch degrades to "no fee" and can never leave both fees
 * on. Hence tuples, whose order is explicit, rather than an object literal
 * whose order is incidental. */
const MODE_WRITES: Record<FeeMode, ReadonlyArray<readonly [string, string]>> = {
  // saveFeeMode appends the merchant's Freepay debit choice after these.
  freepay: [
    [KEYS.enabled, "false"],
    [KEYS.freepayCredit, "true"],
  ],
  customFee: [
    [KEYS.freepayCredit, "false"],
    [KEYS.freepayDebit, "false"],
    [KEYS.enabled, "true"],
  ],
  // Nothing is turned on, so any prefix of this is already a safe state.
  none: [
    [KEYS.freepayCredit, "false"],
    [KEYS.freepayDebit, "false"],
    [KEYS.enabled, "false"],
  ],
};

/** The per-method Custom Fee schema is honoured as soon as ANY of these rows
 * exists. Credit's rate is not among them, because it is read from KEYS.percent
 * in both schemas — one rate, one row, so a rate saved on an older terminal is
 * never shadowed by a newer row holding the same number under another name.
 *
 * A partial set is harmless: an absent enabled flag reads as OFF (see `enabled`
 * below) and an absent rate reads as zero, so every key an interrupted save
 * failed to land charges less, never more. */
const PER_METHOD_KEYS: readonly string[] = [
  KEYS.creditEnabled,
  KEYS.debitEnabled,
  KEYS.debitPercent,
  KEYS.debitFlatCents,
];

export interface TerminalBehaviorSettings {
  invoiceNumberRequired: boolean;
  tip: {
    enabled: boolean;
    /** Raw JSON array of {type: "percent"|"fixed", value: number}. */
    presetsJson: string;
    allowCustom: boolean;
    noTipEnabled: boolean;
  };
  tax: { enabled: boolean; percent: number; label: string; included: boolean };
  cashEnabled: boolean;
  receipt: {
    header: string;
    footer: string;
    showLogo: boolean;
    autoMerchantCopy: boolean;
  };
}

const BEHAVIOR_KEYS = {
  invoiceNumberRequired: "invoice_number_required",
  tipEnabled: "tip_enabled",
  tipPresets: "tip_presets",
  tipAllowCustom: "tip_allow_custom",
  tipNoTipEnabled: "tip_no_tip_enabled",
  taxEnabled: "tax_enabled",
  taxPercent: "tax_percent",
  taxLabel: "tax_label",
  taxIncluded: "tax_included",
  cashEnabled: "cash_enabled",
  receiptHeader: "receipt_header",
  receiptFooter: "receipt_footer",
  receiptShowLogo: "receipt_show_logo",
  receiptAutoMerchantCopy: "receipt_auto_merchant_copy",
} as const;

export class TerminalSettingsRepository {
  /** Read all TerminalSettings rows for a business as a key→value map.
   * When duplicate rows exist for a key, the one with the newer updatedAt wins. */
  private async readMap(
    businessId: string,
  ): Promise<Map<string, { id: string; value: string }>> {
    const map = new Map<
      string,
      { id: string; value: string; updatedAt: string }
    >();
    let nextToken: string | null | undefined;
    do {
      const {
        data,
        nextToken: nt,
        errors,
      } = await client.models.TerminalSettings.listTerminalSettingsByBusinessIdAndSettingsKey(
        { businessId },
        { limit: 500, nextToken },
      );
      if (errors?.length) throw new Error(errors[0].message);
      for (const row of data ?? []) {
        const existing = map.get(row.settingsKey);
        if (!existing || (row.updatedAt ?? "") > (existing.updatedAt ?? "")) {
          map.set(row.settingsKey, {
            id: row.id,
            value: row.settingsValue,
            updatedAt: row.updatedAt ?? "",
          });
        }
      }
      nextToken = nt;
    } while (nextToken);
    return map;
  }

  /** The mode is derived, never stored. Freepay wins when both flags are on. */
  async deriveFeeMode(businessId: string): Promise<FeeMode> {
    const { mode } = await this.getFeeModeState(businessId);
    return mode;
  }

  /** Like deriveFeeMode, but also reports a literal both-on legacy state so a
   * caller can warn before a save silently drops one fee. */
  async getFeeModeState(businessId: string): Promise<FeeModeState> {
    const map = await this.readMap(businessId);
    const freepayOn = map.get(KEYS.freepayCredit)?.value === "true";
    const customOn = map.get(KEYS.enabled)?.value === "true";
    return {
      mode: freepayOn ? "freepay" : customOn ? "customFee" : "none",
      bothOn: freepayOn && customOn,
      freepayDebit: map.get(KEYS.freepayDebit)?.value === "true",
    };
  }

  /** `freepayDebit` is written only when `mode` is freepay — after the credit
   * flag, so an interrupted batch leaves a Freepay state with the previous
   * debit choice rather than a debit flag on its own. */
  async saveFeeMode(
    businessId: string,
    mode: FeeMode,
    freepayDebit: boolean,
  ): Promise<void> {
    const existing = await this.readMap(businessId);
    const writes =
      mode === "freepay"
        ? [...MODE_WRITES.freepay, [KEYS.freepayDebit, String(freepayDebit)]]
        : // eslint-disable-next-line security/detect-object-injection -- mode is a FeeMode literal, not user input
          MODE_WRITES[mode];
    for (const [key, value] of writes) {
      await this.upsert(businessId, existing, key, value);
    }
  }

  async getCustomFee(businessId: string): Promise<CustomFeeSettings> {
    const map = await this.readMap(businessId);
    const get = (key: string) => map.get(key)?.value;
    const rate = (p?: string, f?: string): MethodRate => ({
      percent: Number.isFinite(parseFloat(p ?? "")) ? parseFloat(p!) : 0,
      flatCents: Number.isFinite(parseInt(f ?? "", 10)) ? parseInt(f!, 10) : 0,
    });
    const name = get(KEYS.name)?.trim() || DEFAULTS.name;
    // An absent flag is OFF. Any stored value keeps the terminal's default-true
    // idiom, so a row that exists behaves exactly as it always has.
    const enabled = (key: string) => {
      const raw = get(key);
      return raw === undefined ? false : raw !== "false";
    };

    const credit = rate(get(KEYS.percent), get(KEYS.flatCents));

    if (PER_METHOD_KEYS.some((key) => get(key) !== undefined)) {
      return {
        name,
        creditEnabled: enabled(KEYS.creditEnabled),
        credit,
        debitEnabled: enabled(KEYS.debitEnabled),
        debit: rate(get(KEYS.debitPercent), get(KEYS.debitFlatCents)),
      };
    }

    if (get(KEYS.percent) !== undefined || get(KEYS.flatCents) !== undefined) {
      return {
        name,
        creditEnabled: true,
        credit,
        debitEnabled: true,
        debit: credit,
      };
    }

    return { ...DEFAULTS, name };
  }

  /** TerminalSettings.category for newly created rows, by key prefix. */
  private categoryFor(
    key: string,
  ): "payment" | "tips" | "tax" | "receipt" | "general" {
    if (key.startsWith("tip_")) return "tips";
    if (key.startsWith("tax_")) return "tax";
    if (key.startsWith("receipt_")) return "receipt";
    if (
      key.startsWith("freepay_") ||
      key.startsWith("custom_fee_") ||
      key === "cash_enabled" ||
      key === "invoice_number_required"
    ) {
      return "payment";
    }
    return "general";
  }

  /** Upsert a single key for a business. */
  private async upsert(
    businessId: string,
    existing: Map<string, { id: string; value: string }>,
    key: string,
    value: string,
  ): Promise<void> {
    const found = existing.get(key);
    // The model's owner rule is `identityClaim("custom:businessId")`, so a row
    // an admin touches (who authorizes via group, not owner) must carry the
    // business id as owner or the merchant's terminal cannot match it — the
    // key would be written and never read. Updates set it too, to repair rows
    // an admin created before this was true.
    if (found) {
      const { errors } = await client.models.TerminalSettings.update({
        id: found.id,
        settingsValue: value,
        owner: businessId,
      });
      if (errors?.length) throw new Error(errors[0].message);
    } else {
      const { errors } = await client.models.TerminalSettings.create({
        businessId,
        settingsKey: key,
        settingsValue: value,
        category: this.categoryFor(key),
        owner: businessId,
      });
      if (errors?.length) throw new Error(errors[0].message);
    }
  }

  async saveCustomFee(businessId: string, s: CustomFeeSettings): Promise<void> {
    for (const [label, r] of [
      ["credit", s.credit],
      ["debit", s.debit],
    ] as const) {
      if (!Number.isFinite(r.percent) || r.percent < 0) {
        throw new Error(`Invalid ${label} percent: ${r.percent}`);
      }
      if (!Number.isInteger(r.flatCents) || r.flatCents < 0) {
        throw new Error(`Invalid ${label} flatCents: ${r.flatCents}`);
      }
    }

    const debitEnabled = s.creditEnabled && s.debitEnabled;
    // Row-at-a-time writes again, so again the order is the safety mechanism.
    // Credit's rate goes last, strictly after the whole PER_METHOD_KEYS set,
    // because a business with none of those rows is still read as one rate on
    // both methods — updating credit first would publish the new rate to debit
    // too. Until the set lands, an interrupted save leaves yesterday's fee; a
    // flag that arrives before its rate reads as zero or as yesterday's value.
    // Either way an interrupted save charges less, never more.
    const entries: ReadonlyArray<readonly [string, string]> = [
      [KEYS.name, s.name.trim() || DEFAULTS.name],
      [KEYS.debitPercent, String(s.debit.percent)],
      [KEYS.debitFlatCents, String(s.debit.flatCents)],
      [KEYS.creditEnabled, String(s.creditEnabled)],
      [KEYS.debitEnabled, String(debitEnabled)],
      [KEYS.percent, String(s.credit.percent)],
      [KEYS.flatCents, String(s.credit.flatCents)],
    ];

    const existing = await this.readMap(businessId);
    for (const [key, value] of entries) {
      await this.upsert(businessId, existing, key, value);
    }
  }

  async getBehaviorSettings(
    businessId: string,
  ): Promise<TerminalBehaviorSettings> {
    const map = await this.readMap(businessId);
    const get = (key: string) => map.get(key)?.value;
    const rawTaxPercent = parseFloat(get(BEHAVIOR_KEYS.taxPercent) ?? "");
    return {
      invoiceNumberRequired:
        get(BEHAVIOR_KEYS.invoiceNumberRequired) === "true",
      tip: {
        enabled: get(BEHAVIOR_KEYS.tipEnabled) === "true",
        presetsJson: get(BEHAVIOR_KEYS.tipPresets) ?? "",
        allowCustom: get(BEHAVIOR_KEYS.tipAllowCustom) === "true",
        // Terminal default: noTipEnabled != 'false' → absent means true
        noTipEnabled: get(BEHAVIOR_KEYS.tipNoTipEnabled) !== "false",
      },
      tax: {
        enabled: get(BEHAVIOR_KEYS.taxEnabled) === "true",
        percent: Number.isNaN(rawTaxPercent) ? 0 : rawTaxPercent,
        label: get(BEHAVIOR_KEYS.taxLabel) ?? "",
        included: get(BEHAVIOR_KEYS.taxIncluded) === "true",
      },
      // Terminal default: cashEnabled != 'false' → absent means true
      cashEnabled: get(BEHAVIOR_KEYS.cashEnabled) !== "false",
      receipt: {
        header: get(BEHAVIOR_KEYS.receiptHeader) ?? "",
        footer: get(BEHAVIOR_KEYS.receiptFooter) ?? "",
        showLogo: get(BEHAVIOR_KEYS.receiptShowLogo) === "true",
        autoMerchantCopy: get(BEHAVIOR_KEYS.receiptAutoMerchantCopy) === "true",
      },
    };
  }

  async saveInvoiceNumberRequired(
    businessId: string,
    required: boolean,
  ): Promise<void> {
    const existing = await this.readMap(businessId);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.invoiceNumberRequired,
      String(required),
    );
  }

  async saveTipSettings(
    businessId: string,
    s: TerminalBehaviorSettings["tip"],
  ): Promise<void> {
    const existing = await this.readMap(businessId);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.tipEnabled,
      String(s.enabled),
    );
    // Empty means "no presets": write '[]' (the terminal treats it the same
    // as an absent key) so clearing the input actually clears stored presets.
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.tipPresets,
      s.presetsJson || "[]",
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.tipAllowCustom,
      String(s.allowCustom),
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.tipNoTipEnabled,
      String(s.noTipEnabled),
    );
  }

  async saveTaxSettings(
    businessId: string,
    s: TerminalBehaviorSettings["tax"],
  ): Promise<void> {
    if (!Number.isFinite(s.percent) || s.percent < 0) {
      throw new Error(`Invalid tax percent: ${s.percent}`);
    }
    const existing = await this.readMap(businessId);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.taxEnabled,
      String(s.enabled),
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.taxPercent,
      String(s.percent),
    );
    await this.upsert(businessId, existing, BEHAVIOR_KEYS.taxLabel, s.label);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.taxIncluded,
      String(s.included),
    );
  }

  async saveCashEnabled(businessId: string, enabled: boolean): Promise<void> {
    const existing = await this.readMap(businessId);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.cashEnabled,
      String(enabled),
    );
  }

  async saveReceiptSettings(
    businessId: string,
    s: TerminalBehaviorSettings["receipt"],
  ): Promise<void> {
    const existing = await this.readMap(businessId);
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.receiptHeader,
      s.header,
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.receiptFooter,
      s.footer,
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.receiptShowLogo,
      String(s.showLogo),
    );
    await this.upsert(
      businessId,
      existing,
      BEHAVIOR_KEYS.receiptAutoMerchantCopy,
      String(s.autoMerchantCopy),
    );
  }
}
